import {
  Alert, Box, Divider, Link, ListItemIcon, ListItemText, Typography, Tooltip
} from "@mui/material";
import { ReactElement } from "react";
import CabinetPage from "../../components/Common/CabinetPage";
import colors from "../../colors";
import { useMemo, useState } from "react";
import CabAvatar from "@CabComponents/CabAvatar";
import { CabButton } from "@CabComponents/CabButton";
import { CabImageUpload } from "@CabComponents";
import { CabIcon } from "@CabComponents/CabIcon";
import {
  IoAdd, IoCalendarOutline, IoClose, IoCloseCircleOutline, IoDocumentTextOutline, IoPeopleOutline,
  IoTimeOutline, IoPersonOutline, IoBusinessOutline, IoEllipsisHorizontal, IoFlashOff
} from "react-icons/io5";
import { ContactDenorm, ContactOrgDenorm, Leader, LeaderRelationshipDenorm } from "../../store";
import { CabTabs } from "@CabComponents/CabTabs";
import { CabCheckbox } from "@CabComponents/CabCheckbox";
import { CabTextInput } from "@CabComponents/CabTextInput";
import { CabToggleChip } from "@CabComponents/CabToggleChip";
import { CabDropdown } from "@CabComponents/CabDropdown";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import {
  AppCrmContactNotesPartialUpdateApiArg,
  AppCrmContactTasksPartialUpdateApiArg,
  AppCrmLeaderRelationshipsPartialUpdateApiArg,
  Contact, ContactFieldTemplate, ContactNote, ContactTask, ContactType, LeaderRelationship
} from "../../store/cabinetApi/generated/crm";
import CabSpinner from "@CabComponents/CabSpinner";
import { DependentController, FormController } from "../../utils/formUtils";
import { CabModal } from "@CabComponents/CabModal";
import { Trigger } from "../../store/utils";
import CabCollapseMenu from "@CabComponents/CabCollapseMenu";
import { AnalyticsEvent } from "../../store/cabinetApi/generated/analytics";
import { DateTime } from "luxon";
import { BasicInfoListItem, WidgetList, WidgetListItem } from "../../components/CRM/SharedCrmComponents";
import { CabTooltip } from "@CabComponents/CabTooltip";
import { getLeaderIconSrc } from "../../utils/leaderUtils";
import { CabExecPicker } from "@CabComponents/CabExecPicker";
import CabRTEditor from "@CabComponents/CabRTEditor";
import BackButton from "@CabComponents/CabBackButton";
import { formatTimezoneOptionsFromString, getLocalTimeZone } from "../../utils/scheduleUtils";
import EventDetailModalContainer from "./EventDetailModal";
import { useConfirmationModalWithUI } from "../../components/Common/ConfirmationModal";
import MergeContactsModal from "../../components/CRM/MergeContactsModal";
import { CabDatePicker } from "@CabComponents/CabDatePicker";
import { CabTimePicker } from "@CabComponents/CabTimePicker";
import { DndContext, useSensor, PointerSensor, closestCenter, DragEndEvent, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';


const tabs = [
  { label: 'Activity' },
  { label: 'Tasks' },
  { label: 'Notes' },
  { label: 'Meetings' },
];


// eslint-disable-next-line max-len
const EMPTY_STATE = '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';


const isRT = (text: string) => {
  try {
    JSON.parse(text);
  } catch (err) {
    return false;
  }
  return true;
};

// Type that allows File objects for avatar uploads
type ContactWithAvatarFile = Omit<Contact, 'avatar'> & {
  avatar?: string | null | File;
};

const CRMContactView = ({
  contact, orgOptions, typeOptions, tagOptions, fieldTemplates,
  onNavigateToContacts, onNavigateToDataView, onNavigateToSettings, onNavigateToAnalyticsSettings, onSaveBasicInfo, 
  loading, loadingEvents, contactOrgPage, tasks, notes, events, onAddNote, onUpdateNote, 
  onDeleteNote, onAddTask, onUpdateTask, onDeleteTask, onSetOrgString, relationshipLeaders, 
  leaderOptions, onUpdateRelationships, crmPreferencesData, onUpdateCrmPreferences, isAdmin,
  relationship, onUpdateRelationship, relationshipTagOptions,
  mergeDialogState, onMergeContacts, onCloseMergeDialog,
}: {
  contact: ContactDenorm | null;
  onNavigateToContacts: () => void;
  onNavigateToDataView: (category: string) => void;
  onNavigateToSettings: () => void;
  onNavigateToAnalyticsSettings: () => void;
  onSaveBasicInfo: (info: Partial<ContactWithAvatarFile>) => void;
  orgOptions: ContactOrgDenorm[];
  typeOptions: ContactType[];
  tagOptions: Array<{ id: number; name: string; color?: string }>;
  fieldTemplates: ContactFieldTemplate[];
  loading: boolean;
  tasks: ContactTask[];
  notes: ContactNote[];
  events: AnalyticsEvent[];
  leaderOptions: Leader[];
  relationshipLeaders: Leader[];
  loadingEvents: boolean;
  contactOrgPage?: string;
  onAddNote: Trigger<ContactNote, ContactNote>;
  onUpdateNote: Trigger<AppCrmContactNotesPartialUpdateApiArg, ContactNote>;
  onDeleteNote: Trigger<ContactNote['id'], unknown>;
  onAddTask: (t: { title: string; due_date?: string | null }) => Promise<void>;
  onUpdateTask: Trigger<AppCrmContactTasksPartialUpdateApiArg, ContactTask>;
  onDeleteTask: Trigger<ContactTask['id'], unknown>;
  onSetOrgString: (val: string) => void;
  onUpdateRelationships: (leaderRelationships: Leader['id'][]) => Promise<void>;
  crmPreferencesData: Array<{ id: number; contact_detail_field_order: string[] }> | undefined;
  onUpdateCrmPreferences: (args: {
    id: number;
    patchedCrmPreferences: { contact_detail_field_order: string[] }
  }) => void;
  isAdmin: boolean;
  relationship?: LeaderRelationshipDenorm;
  onUpdateRelationship?: Trigger<AppCrmLeaderRelationshipsPartialUpdateApiArg, LeaderRelationship>;
  relationshipTagOptions: Array<{ id: number; name: string; color?: string }>;
  mergeDialogState: {
    existingContact: Contact | null;
    isMerging: boolean;
  };
  onMergeContacts: () => Promise<void>;
  onCloseMergeDialog: () => void;
}) => {
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [editNote, setEditNote] = useState<ContactNote['id'] | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [editTask, setEditTask] = useState<ContactTask['id'] | null>(null);
  const [showRelationshipsModal, setShowRelationshipsModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<AnalyticsEvent | null>(null);
  const [openEditAvatarModal, setOpenEditAvatarModal] = useState(false);
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [avatar, setAvatar] = useState<{ file: File, fileName: string } | null>();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  
  const { confirm: confirmDelete, ConfirmationModalComponent: DeleteConfirmationModal } = useConfirmationModalWithUI();

  const activities = useMemo(() => (
    [...tasks, ...notes, ...events]
      .map(a => ({
        ...a,
        dueDate: 'due_date' in a ? a.due_date : undefined,
        sortDate: 'assignee' in a ? a.created_at
          : 'body' in a ? a.updated_at
            : 'event_idr' in a ? a.start_date
              : undefined
      }))
      .toSorted((a, b) => {
        const now = DateTime.now();
        const cutoff = now.plus({ hours: 24 });
        
        const aUrgent = a.dueDate && DateTime.fromISO(a.dueDate) <= cutoff;
        const bUrgent = b.dueDate && DateTime.fromISO(b.dueDate) <= cutoff;
        
        if (aUrgent && !bUrgent) return -1;
        if (!aUrgent && bUrgent) return 1;
        if (aUrgent && bUrgent) {
          return DateTime.fromISO(a.dueDate!).toMillis() - DateTime.fromISO(b.dueDate!).toMillis();
        }
        
        const sortDateA = a.sortDate ? DateTime.fromISO(a.sortDate) : undefined;
        const sortDateB = b.sortDate ? DateTime.fromISO(b.sortDate) : undefined;
        if (!sortDateA) return 1;
        if (!sortDateB) return -1;

        return sortDateB.toMillis() - sortDateA.toMillis();
      })
  ), [tasks, notes, events]);

  const handleAddEditNote = async ({ body }: { body: string }) => {
    if (!contact) return;

    if (editNote != null) {
      await onUpdateNote({ id: editNote, patchedContactNote: { body } });
      setEditNote(null);
    } else {
      await onAddNote({
        id: -1,
        body: body,
        updated_at: '',
        body_plain_text: '',
        contacts: [contact.id],
        teammates: [],
        company: [],
        allowed_users: [],
      });
    }

    setShowNoteModal(false);
  };

  const handleAddEditNoteCancel = () => {
    setEditNote(null);
    setShowNoteModal(false);
  };

  const handleClickNote = (id: ContactNote['id']) => {
    setEditNote(id);
    setShowNoteModal(true);
  };

  const handleAddEditTask = async ({ title, due_date }: { title: string; due_date?: string | null }) => {
    if (editTask != null) {
      await onUpdateTask({ id: editTask, patchedContactTask: { title, due_date } });
      setEditTask(null);
    } else {
      await onAddTask({
        title: title,
        due_date: due_date,
      });
    }

    setShowTaskModal(false);
  };

  const handleAddEditTaskCancel = () => {
    setEditTask(null);
    setShowTaskModal(false);
  };

  const handleClickTask = (id: ContactTask['id']) => {
    setEditTask(id);
    setShowTaskModal(true);
  };

  const handleClickActivity = (id: ContactTask['id'] | ContactNote['id'], activityType: 'task' | 'note') => {
    if (activityType === 'task') {
      handleClickTask(id);
    } else {
      handleClickNote(id);
    }
  };


  const handleDeleteTask = async (id: ContactTask['id']) => {
    const confirmed = await confirmDelete({
      title: "Confirm Deletion",
      message: "Are you sure you want to delete this task?",
      confirmText: "Delete", cancelText: "Cancel", isDestructive: true,
    });
    if (confirmed) {
      onDeleteTask(id);
    }
  };

  const handleDeleteNote = async (id: ContactNote['id']) => {
    const confirmed = await confirmDelete({
      title: "Confirm Deletion",
      message: "Are you sure you want to delete this note?",
      confirmText: "Delete", cancelText: "Cancel", isDestructive: true,
    });
    if (confirmed) {
      onDeleteNote(id);
    }
  };

  const handleDeleteActivity = async (id: ContactTask['id'] | ContactNote['id'], activityType: 'task' | 'note') => {
    if (activityType === 'task') {
      handleDeleteTask(id);
    } else {
      handleDeleteNote(id);
    }
  };

  const handleUpdateRelationships = async ({ leaderRelationships }: { leaderRelationships: Pick<Leader, 'id'>[] }) => {
    await onUpdateRelationships(leaderRelationships.map(l => l.id));
    setShowRelationshipsModal(false);
  };

  const handleClickEvent = (event: AnalyticsEvent) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const handleCloseEventModal = () => {
    setShowEventModal(false);
    setSelectedEvent(null);
  };

  const handleCancelEditAvatar = () => {
    setAvatarUrl(null);
    setAvatar(null);
    setOpenEditAvatarModal(false);
  };

  const handleSubmitAvatar = async () => {
    if (avatar?.file && contact) {
      await onSaveBasicInfo({ avatar: avatar.file });
    }
    setOpenEditAvatarModal(false);
  };

  return (
    <CabinetPage
      pageName="Contact"
      headerContent={
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between'
          }}>
          <BackButton />
          <CabCollapseMenu
            popOverSx={theme => ({ marginTop: theme.spacing(0.5), width: 500 })}
            target={
              <CabButton
                buttonType="primary"
                icon={<CabIcon Icon={IoAdd} alt=''/>}
              >
                Create
              </CabButton>
            }
            buttons={
              <Box sx={{
                width: 260
              }}>
                <CabButton 
                  buttonType="text"
                  onClick={() => setShowTaskModal(true)}
                  sx={{ width: '100%', '& .button-text': { width: '100%', color: colors.black900, textAlign: 'left' } }}
                >
                  <Typography variant='h2'>Task</Typography>
                  <Typography variant="body2" color={colors.black800}>
                    Set up a task with a due date that can be checked off when completed.
                  </Typography> 
                </CabButton>
                <Divider sx={{backgroundColor: colors.black200}}/>
                <CabButton 
                  buttonType="text"
                  onClick={() => setShowNoteModal(true)}
                  sx={{ width: '100%', '& .button-text': { width: '100%', color: colors.black900, textAlign: 'left' } }}
                >
                  <Typography variant='h2' >Note</Typography>
                  <Typography variant="body2" color={colors.black800}>
                    Jot down a note for this contact
                  </Typography> 
                </CabButton>
              </Box>
            }
            popOverTitle=""
          />

        </Box>
      }
    >
      {contact ? (
        <Box
          sx={{
            display: "flex",
            height: "100%",
            width: "100%",
            gap: 3,
            flexDirection: "column"
          }}>
          <Box
            sx={{
              display: "flex",
              flex: 12,
              flexDirection: { xs: 'column', md: 'row' },
              overflow: "hidden"
            }}>
            <Box
              sx={{
                display: "flex",
                flex: 2,
                paddingLeft: 3,
                paddingTop: 1,
                overflow: "auto",
                maxHeight: '100%'
              }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  width: "100%",
                  paddingRight: 1
                }}>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    padding: 2,
                    paddingLeft: 1
                  }}>
                  <Box sx={{ cursor: 'pointer' }}>
                    <CabAvatar 
                      name={contact?.name || ''} 
                      color={colors.black500} 
                      src={contact.avatar}
                      onClick={() => setOpenEditAvatarModal(true)}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column"
                    }}>
                    <Typography variant="h1">{contact?.name || contact.primary_email}</Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1.5,
                        marginTop: 0.5,
                        rowGap: 0.5,
                        columnGap: 1.5
                      }}>
                      {contact.title && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            minWidth: 0,
                            maxWidth: "100%"
                          }}>
                          <CabIcon Icon={IoPersonOutline} size="small" color={colors.black500} />
                          <Typography 
                            noWrap
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              minWidth: 0
                            }}
                          >
                            {contact.title}
                          </Typography>
                        </Box>
                      )}
                      {contact.company?.name && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            minWidth: 0,
                            maxWidth: "100%"
                          }}>
                          <CabIcon Icon={IoBusinessOutline} size="small" color={colors.black500} />
                          <Typography 
                            noWrap
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              minWidth: 0
                            }}
                          >
                            {contact.company.name}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
                {/* About this Relationship Header */}
                {relationship && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingTop: 1,
                        paddingX: 1
                      }}>
                      <Typography variant="h2" color={colors.black900}>
                        About this Relationship
                      </Typography>
                    </Box>
                    <RelationshipInfoView
                      relationship={relationship}
                      onUpdateRelationship={onUpdateRelationship}
                      relationshipTagOptions={relationshipTagOptions}
                      onNavigateToSettings={onNavigateToSettings}
                    />
                  </>
                )}
                {/* About this Contact Header */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    paddingTop: 1,
                    paddingX: 1
                  }}>
                  <Typography variant="h2" color={colors.black900}>
                    About this Contact
                  </Typography>
                  {isEditingOrder ? (
                    <CabButton
                      buttonType="secondary"
                      size="small"
                      onClick={() => setIsEditingOrder(false)}
                      sx={{ minWidth: 'auto' }}
                    >
                      Done Ordering
                    </CabButton>
                  ) : (
                    <CabCollapseMenu
                      popOverSx={theme => ({ marginTop: theme.spacing(0.5), width: 200 })}
                      target={
                        <CabButton
                          buttonType="text"
                          icon={<CabIcon Icon={IoEllipsisHorizontal} alt=''/>}
                          sx={{ 
                            minWidth: 'auto', 
                            paddingX: 1,
                            '& .MuiButton-icon': {
                              padding: 0,
                              margin: 0
                            }
                          }}
                        />
                      }
                      buttons={
                        <Box sx={{
                          width: "100%"
                        }}>
                          <CabButton 
                            buttonType="text"
                            onClick={() => setIsEditingOrder(true)}
                            sx={{ 
                              width: '100%', 
                              whiteSpace: 'nowrap',
                              '& .button-text': { 
                                width: '100%', 
                                color: colors.black900, 
                                textAlign: 'left',
                                whiteSpace: 'nowrap'
                              } 
                            }}
                          >
                            <Typography variant="body1">Edit Order</Typography>
                          </CabButton>
                          <Divider sx={{backgroundColor: colors.black200}}/>
                          <Tooltip title={!isAdmin ? "Requires Admin Permissions" : ""} placement="top">
                            <span>
                              <CabButton 
                                buttonType="text"
                                onClick={isAdmin ? onNavigateToSettings : undefined}
                                disabled={!isAdmin}
                                sx={{ 
                                  width: '100%', 
                                  whiteSpace: 'nowrap',
                                  opacity: isAdmin ? 1 : 0.5,
                                  cursor: isAdmin ? 'pointer' : 'not-allowed',
                                  '& .button-text': { 
                                    width: '100%', 
                                    color: isAdmin ? colors.black900 : colors.black400, 
                                    textAlign: 'left',
                                    whiteSpace: 'nowrap'
                                  },
                                  '&:hover': {
                                    backgroundColor: isAdmin ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                                  }
                                }}
                              >
                                <Typography variant="body1" sx={{ color: isAdmin ? colors.black900 : colors.black400 }}>
                                  Edit Properties
                                </Typography>
                              </CabButton>
                            </span>
                          </Tooltip>
                          <Divider sx={{backgroundColor: colors.black200}}/>
                          <Tooltip title={!isAdmin ? "Requires Admin Permissions" : ""} placement="top">
                            <span>
                              <CabButton 
                                buttonType="text"
                                onClick={isAdmin ? onNavigateToAnalyticsSettings : undefined}
                                disabled={!isAdmin}
                                sx={{ 
                                  width: '100%', 
                                  whiteSpace: 'nowrap',
                                  opacity: isAdmin ? 1 : 0.5,
                                  cursor: isAdmin ? 'pointer' : 'not-allowed',
                                  '& .button-text': { 
                                    width: '100%', 
                                    color: isAdmin ? colors.black900 : colors.black400, 
                                    textAlign: 'left',
                                    whiteSpace: 'nowrap'
                                  },
                                  '&:hover': {
                                    backgroundColor: isAdmin ? 'rgba(0, 0, 0, 0.04)' : 'transparent'
                                  }
                                }}
                              >
                                <Typography variant="body1" sx={{ color: isAdmin ? colors.black900 : colors.black400 }}>
                                  Edit Categories
                                </Typography>
                              </CabButton>
                            </span>
                          </Tooltip>
                        </Box>
                      }
                      popOverTitle=""
                    />
                  )}
                </Box>
                <BasicInfoView
                  contact={contact}
                  onSave={onSaveBasicInfo}
                  orgOptions={orgOptions}
                  typeOptions={typeOptions}
                  tagOptions={tagOptions}
                  fieldTemplates={fieldTemplates}
                  onSetOrgString={onSetOrgString}
                  contactOrgPage={contactOrgPage}
                  crmPreferencesData={crmPreferencesData}
                  onUpdateCrmPreferences={onUpdateCrmPreferences}
                  onNavigateToSettings={onNavigateToSettings}
                  isEditingOrder={isEditingOrder}
                />
              </Box>
            </Box>
            <Divider orientation="vertical" flexItem sx={{ backgroundColor: colors.black200 }} />
            <Box
              sx={{
                display: "flex",
                flex: 4,
                flexDirection: "column",
                gap: 3,
                overflow: "auto",
                paddingLeft: 1,
                maxHeight: '100%'
              }}>
              <Box
                sx={{
                  display: "flex",
                  flex: 1,
                  minHeight: 0
                }}>
                <CabTabs
                  tabs={tabs} align="left" hideDivider
                  sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}
                  tabPanelSx={{ paddingTop: 2, flex: 1, minHeight: 0, overflow: 'auto' }}
                >
                  <ActivitiesView
                    activities={activities}
                    onDeleteActivity={handleDeleteActivity}
                    onUpdateActivity={onUpdateTask}
                    onClickActivity={handleClickActivity}
                    onClickEvent={handleClickEvent}
                  />
                  <TasksView
                    tasks={tasks}
                    onDeleteTask={handleDeleteTask}
                    onUpdateTask={onUpdateTask}
                    onClickTask={handleClickTask}
                    onAddTask={() => setShowTaskModal(true)}
                  />
                  <NotesView
                    notes={notes}
                    onDeleteNote={handleDeleteNote}
                    onClickNote={handleClickNote}
                    onAddNote={() => setShowNoteModal(true)}
                  />
                  {loadingEvents ? (
                    <Box
                      sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                      <CabSpinner scale={4} color='inherit' />
                    </Box>
                  ) : (
                    <MeetingsView events={events} onClickEvent={handleClickEvent}/>
                  )}
                </CabTabs>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          hidden={loading}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: 5
          }}>
          <Alert severity="warning">
            Contact not found
          </Alert>
        </Box>
      )}
      <Box
        hidden={!loading}
        sx={{
          height: '100%',
          display: 'flex',
          alignItems: 'center'
        }}>
        <CabSpinner scale={4} color='inherit' />
      </Box>
      {showNoteModal && (
        <AddNoteModal
          open={showNoteModal}
          onClose={handleAddEditNoteCancel}
          onSubmit={handleAddEditNote}
          note={editNote != null ? notes.find(n => n.id === editNote) : undefined}
        />
      )}
      {showTaskModal && (
        <AddTaskModal
          open={showTaskModal}
          onClose={handleAddEditTaskCancel}
          onSubmit={handleAddEditTask}
          task={editTask != null ? tasks.find(t => t.id === editTask) : undefined}
        />
      )}
      {showRelationshipsModal && (
        <EditRelationshipsModal
          open={showRelationshipsModal}
          onClose={() => setShowRelationshipsModal(false)}
          onSubmit={handleUpdateRelationships}
          leaderOptions={leaderOptions}
          leaderRelationships={relationshipLeaders}
        />
      )}
      {showEventModal && (
        <EventDetailModalContainer
          open={showEventModal}
          onClose={handleCloseEventModal}
          event={selectedEvent}
        />
      )}
      <CabModal 
        open={openEditAvatarModal}
        onClose={() => setOpenEditAvatarModal(false)}
        title={'Update Contact Avatar'}
        closeIcon
        closeOnBackdropClick
        actionButtons={
          <>
            <CabButton buttonType='secondary' onClick={handleCancelEditAvatar}>Cancel</CabButton>
            <CabButton onClick={handleSubmitAvatar}>Save</CabButton>
          </>
        }
      >
        <CabImageUpload
          currentImage={avatarUrl || contact?.avatar}
          avatarName={contact?.name || contact?.primary_email || ''}
          displayMode="avatar"
          avatarSize="large"
          onImageSelect={(file) => {
            setAvatarUrl(URL.createObjectURL(file));
            setAvatar({ file, fileName: file.name });
          }}
          dropzoneSx={{
            flexDirection: 'row',
            gap: 2,
            padding: 2,
            marginBottom: 1,
            border: `1px solid ${colors.black200}`,
            borderRadius: 1
          }}
          previewSx={{ height: '80px', width: '80px' }}
          dragDropText="Drag and drop file or"
          clickText="click here"
          fileTypeText="JPG, GIF or PNG. Max size of"
          maxSizeText="2.5Mb"
        />
      </CabModal>
      <DeleteConfirmationModal />
      {mergeDialogState.existingContact && (
        <MergeContactsModal
          open={true}
          onClose={onCloseMergeDialog}
          onConfirm={onMergeContacts}
          currentContactName={contact?.name}
          existingContactName={mergeDialogState.existingContact.name}
          email={mergeDialogState.existingContact.primary_email || ''}
          isMerging={mergeDialogState.isMerging}
        />
      )}
    </CabinetPage>
  );
};

const relationshipStrengthNumToStr = (value: number | string) => {
  const numValue = typeof value === 'string' ? Number(value) : value;
  if (numValue >= 7) return 'Strong';
  if (numValue >= 3) return 'Good';
  if (numValue >= 0) return 'Weak';
  return 'Auto';
};

const RelationshipStrengthField = ({
  title,
  value,
  displayValue,
  colorValue,
  options,
  isOverridden,
  autoValue,
  onSave,
  readOnly
}: {
  title: string;
  value: string;
  displayValue: string;
  colorValue: string;
  options: Array<{ value: string; label: string; color: string }>;
  isOverridden: boolean;
  autoValue: string | null | undefined;
  onSave: (val: string | number | number[] | string[] | (string | number)[] | null) => void;
  readOnly: boolean;
}) => {
  const [editing, setEditing] = useState(false);
  const selectedOption = options.find(opt => opt.value === colorValue);

  const handleItemClick = () => {
    if (!editing && !readOnly) {
      setEditing(true);
    }
  };

  const handleItemBlur = () => {
    setEditing(false);
  };

  const handleEnter: React.KeyboardEventHandler = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setEditing(false);
    }
  };

  const autoValueDisplay = autoValue ?? 'N/A';
  const tooltipText = 
    `This relationship strength is automatically calculated (${autoValueDisplay}).`;

  return (
    <WidgetListItem
      onClick={readOnly ? undefined : handleItemClick}
      divider={false}
      secondaryAction={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1
          }}>
          {isOverridden && (
            <CabTooltip
              title="This relationship's strength has been set manually. Cabinet will not automatically update it."
              wrapWithSpan
            >
              <CabIcon Icon={IoFlashOff} sx={{ marginTop: 3 }} />
            </CabTooltip>
          )}
        </Box>
      }
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          width: "100%"
        }}>
        <Box sx={{
          flex: 1
        }}>
          <ListItemText
            primary={title}
            secondary={(
              <Box>
                <Box
                  sx={{
                    visibility: editing ? 'visible' : 'hidden',
                    height: editing ? undefined : 0
                  }}>
                  <CabDropdown
                    multiple={false}
                    overrides={{
                      autoFocus: true,
                      onKeyDown: handleEnter,
                    }}
                    onBlur={handleItemBlur}
                    options={options.map(option => ({
                      value: option.value,
                      label: option.color ? (
                        <CabToggleChip chipColor={option.color} label={option.label} selected />
                      ) : option.label
                    }))}
                    value={value}
                    onChange={(e: { target: { value: unknown } }) => {
                      onSave(e.target.value as string);
                      setEditing(false);
                    }}
                    sx={{ width: '100%' }}
                  />
                </Box>
                {!editing && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1
                    }}>
                    {!isOverridden ? (
                      <CabTooltip title={tooltipText} wrapWithSpan>
                        <Box component="span" sx={{ cursor: 'help' }}>
                          {selectedOption?.color ? (
                            <CabToggleChip chipColor={selectedOption.color} label={displayValue} selected />
                          ) : (
                            <Typography sx={{
                              fontSize: 15
                            }}>{displayValue}</Typography>
                          )}
                        </Box>
                      </CabTooltip>
                    ) : (
                      <>
                        {selectedOption?.color ? (
                          <CabToggleChip chipColor={selectedOption.color} label={displayValue} selected />
                        ) : (
                          <Typography sx={{
                            fontSize: 15
                          }}>{displayValue}</Typography>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </Box>
            )}
            slotProps={{
              primary: {
                sx: { fontSize: 12, fontWeight: 'bolder', color: colors.black700, fontFamily: 'Inter' }
              },

              secondary: {
                sx: {
                  cursor: readOnly ? undefined : 'pointer',
                  fontWeight: 'bold',
                  fontSize: 16,
                  color: colors.black900,
                }
              }
            }} />
        </Box>
      </Box>
    </WidgetListItem>
  );
};

const RelationshipInfoView = ({
  relationship, onUpdateRelationship, relationshipTagOptions, onNavigateToSettings
}: {
  relationship: LeaderRelationshipDenorm;
  onUpdateRelationship?: Trigger<AppCrmLeaderRelationshipsPartialUpdateApiArg, LeaderRelationship>;
  relationshipTagOptions: Array<{ id: number; name: string; color?: string }>;
  onNavigateToSettings: () => void;
}) => {
  // Get last meeting from prev_meeting_data
  const lastMeeting = relationship.prev_meeting_data?.['v1_15'] as AnalyticsEvent | undefined;
  const lastMeetingDate = lastMeeting?.start_date || null;

  // Get next meeting from next_meeting_data
  const nextMeeting = relationship.next_meeting_data?.['v1_15'] as AnalyticsEvent | undefined;
  const nextMeetingDate = nextMeeting?.start_date || null;

  // Relationship strength - determine current value and if overridden
  const isOverridden = relationship.relationship_strength_override != null;
  // For dropdown value: use 'auto' when not overridden, otherwise use override
  const relationshipStrengthValue = isOverridden && relationship.relationship_strength_override
    ? relationship.relationship_strength_override 
    : 'auto';
  // For display: use capitalized override or calculated value
  const relationshipStrengthDisplay = isOverridden && relationship.relationship_strength_override
    ? relationship.relationship_strength_override.charAt(0).toUpperCase()
      + relationship.relationship_strength_override.slice(1)
    : relationshipStrengthNumToStr(relationship.relationship_strength);
  // For color lookup: use actual strength value (lowercase), not 'auto'
  const relationshipStrengthColorValue = isOverridden && relationship.relationship_strength_override
    ? relationship.relationship_strength_override
    : relationshipStrengthDisplay.toLowerCase();

  // Relationship strength options with colors (same as list page)
  const relationshipStrengthOptions = [
    { value: 'auto', label: 'Auto', color: colors.black300 },
    { value: 'weak', label: 'Weak', color: '#FCF55F' },
    { value: 'good', label: 'Good', color: colors.blueLight },
    { value: 'strong', label: 'Strong', color: colors.greenPrimary },
  ];

  const handleRelationshipStrengthSave = async (
    val: string | number | number[] | string[] | (string | number)[] | null
  ) => {
    if (!onUpdateRelationship || !relationship.id) return;
    
    const strengthValue = typeof val === 'string' ? val : String(val);
    const overrideValue = strengthValue === 'auto' ? null : strengthValue as 'weak' | 'good' | 'strong';
    
    await onUpdateRelationship({
      id: relationship.id,
      patchedLeaderRelationship: { relationship_strength_override: overrideValue }
    });
  };

  // Cadence display
  const cadenceDisplay = relationship.contact_cadence
    ? `${relationship.contact_cadence.frequency_type?.replace('_', ' ')
      .replace(/\b\w/g, (l) => l.toUpperCase()) || 'Not Set'}`
    : 'Not Set';

  // Cadence status display
  const cadenceStatusDisplay = relationship.cadence_status
    ? relationship.cadence_status.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    : 'Not Set';

  // Relationship tags - get current tags from relationship
  const relationshipTags = relationship.tags || [];
  
  // Map tag IDs to Tag objects for display text
  const selectedTags = relationshipTags
    .map(tagId => relationshipTagOptions.find(tag => tag.id === tagId))
    .filter((tag): tag is { id: number; name: string; color?: string } => !!tag);
  
  const tagsDisplay = selectedTags.length > 0 
    ? selectedTags.map(tag => tag.name).join(', ')
    : 'None';

  const handleTagsSave = async (val: string | number | number[] | string[] | (string | number)[] | null) => {
    if (!onUpdateRelationship || !relationship.id) return;
    
    const CREATE_NEW_TAG_VALUE = 'CREATE_NEW_TAG';
    
    // Check if CREATE_NEW_TAG was selected
    const valArray = Array.isArray(val) ? val : (val ? [val] : []);
    const hasCreateNewTag = valArray.some(v => String(v) === CREATE_NEW_TAG_VALUE);
    if (hasCreateNewTag) {
      onNavigateToSettings();
      // Filter out CREATE_NEW_TAG_VALUE and keep existing tags
      const tagsArray = relationshipTags;
      await onUpdateRelationship({
        id: relationship.id,
        patchedLeaderRelationship: { tags: tagsArray }
      });
      return;
    }
    
    // Convert to array of tag IDs (numbers), filtering out CREATE_NEW_TAG_VALUE
    const tagsArray = valArray
      .filter(v => String(v) !== CREATE_NEW_TAG_VALUE)
      .map(v => typeof v === 'number' ? v : Number(v))
      .filter(v => !isNaN(v));
    
    await onUpdateRelationship({
      id: relationship.id,
      patchedLeaderRelationship: { tags: tagsArray }
    });
  };

  return (
    <WidgetList>
      <BasicInfoListItem
        title="Last Meeting"
        text={lastMeetingDate ? DateTime.fromISO(lastMeetingDate).toLocaleString(DateTime.DATE_FULL) : 'None'}
        inputType="text"
        inputValue={lastMeetingDate || ''}
        onSave={() => {}}
        readOnly
      />
      <BasicInfoListItem
        title="Next Meeting"
        text={nextMeetingDate ? DateTime.fromISO(nextMeetingDate).toLocaleString(DateTime.DATE_FULL) : 'None'}
        inputType="text"
        inputValue={nextMeetingDate || ''}
        onSave={() => {}}
        readOnly
      />
      <RelationshipStrengthField
        title="Relationship Strength"
        value={relationshipStrengthValue}
        displayValue={relationshipStrengthDisplay}
        colorValue={relationshipStrengthColorValue}
        options={relationshipStrengthOptions}
        isOverridden={isOverridden}
        autoValue={relationship.relationship_strength_auto}
        onSave={handleRelationshipStrengthSave}
        readOnly={!onUpdateRelationship}
      />
      <BasicInfoListItem
        title="Cadence"
        text={cadenceDisplay}
        inputType="text"
        inputValue={cadenceDisplay}
        onSave={() => {}}
        readOnly
      />
      <BasicInfoListItem
        title="Cadence Status"
        text={cadenceStatusDisplay}
        inputType="text"
        inputValue={cadenceStatusDisplay}
        onSave={() => {}}
        readOnly
      />
      <BasicInfoListItem
        title="Relationship Tags"
        text={tagsDisplay}
        inputType="multi-select"
        inputValue={relationshipTags}
        selectOptions={[
          ...relationshipTagOptions.map(tag => ({
            value: tag.id,
            label: tag.name,
            color: tag.color
          })),
          {
            value: 'CREATE_NEW_TAG',
            label: '+ Create New Tag',
            color: undefined
          }
        ]}
        placeholder="Select tags..."
        onSave={handleTagsSave}
        readOnly={!onUpdateRelationship}
      />
    </WidgetList>
  );
};

interface BasicInfoForm {
  name: string;
  email: string;
  allEmails: string[];
  company: number | null;
  title: string;
  location: string;
  timezone: string;
  assistant_name: string;
  assistant_email: string;
  phone_number: string;
  linkedin_url: string;
  contactType: number | null;
  tags: number[];
  templateFields: Record<string, { textValue: string | null; choices: number[] | null }>;
}

const BasicInfoView = ({
  contact, orgOptions, typeOptions, tagOptions, fieldTemplates, onSetOrgString, contactOrgPage,
  onSave, crmPreferencesData, onUpdateCrmPreferences, onNavigateToSettings, isEditingOrder
}: {
  contact: ContactDenorm,
  onSave: (info: Partial<ContactWithAvatarFile>) => void;
  orgOptions: ContactOrgDenorm[]; typeOptions: ContactType[];
  tagOptions: Array<{ id: number; name: string; color?: string }>;
  fieldTemplates: ContactFieldTemplate[];
  onSetOrgString: (val: string) => void;
  contactOrgPage?: string;
  crmPreferencesData: Array<{ id: number; contact_detail_field_order: string[] }> | undefined;
  onUpdateCrmPreferences: (args: { 
    id: number; 
    patchedCrmPreferences: { contact_detail_field_order: string[] } 
  }) => void;
  onNavigateToSettings: () => void;
  isEditingOrder: boolean;
}) => {
  const { control, handleSubmit, reset, formState } = useForm<BasicInfoForm>({
    values: {
      name: contact.name || '',
      email: contact.primary_email || '',
      allEmails: contact.all_emails || [],
      company: contact.company?.id || null,
      title: contact.title || '',
      location: contact.location || '',
      timezone: contact.timezone || '',
      assistant_name: contact.assistant_name || '',
      assistant_email: contact.assistant_email || '',
      phone_number: contact.phone_number || '',
      linkedin_url: contact.linkedin_url || '',
      contactType: contact.contact_type?.id || null,
      tags: contact.tags || [],
      templateFields: Object.fromEntries(fieldTemplates.map(template => {
        const field = contact.fields?.[template.id];
        return [template.id, {
          textValue: field?.text_value || null,
          choices: field?.choices || null,
        }];
      })) as Record<number, { textValue: string | null; choices: number[] | null }>,
    }
  });

  // Get current field order from preferences or use default
  const fieldOrder = useMemo(() => {
    // Define default field order
    const defaultFieldOrder = [
      'name',
      'email', 
      'allEmails',
      'company',
      'title',
      'location',
      'timezone',
      'linkedin_url',
      'assistant_name',
      'assistant_email',
      'tags',
      'contactType',
      ...fieldTemplates.map(t => `templateFields.${t.id}`)
    ];

    if (!crmPreferencesData?.[0]?.contact_detail_field_order) {
      return defaultFieldOrder;
    }

    const savedOrder = crmPreferencesData[0].contact_detail_field_order;
    const currentFieldNames = new Set(defaultFieldOrder);
    const savedFieldNames = new Set(savedOrder);

    // Filter out any saved fields that no longer exist
    const validSavedFields = savedOrder.filter((field: string) => currentFieldNames.has(field));
    
    // Add any new fields that weren't in the saved order
    const newFields = defaultFieldOrder.filter(field => !savedFieldNames.has(field));
    
    return [...validSavedFields, ...newFields];
  }, [crmPreferencesData, fieldTemplates]);

  const sensors = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8,
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const newOrder = arrayMove(fieldOrder, 
        fieldOrder.findIndex(item => item === active.id),
        fieldOrder.findIndex(item => item === over.id)
      );

      // Update the order in the API
      if (crmPreferencesData?.[0]) {
        onUpdateCrmPreferences({
          id: crmPreferencesData[0].id,
          patchedCrmPreferences: {
            contact_detail_field_order: newOrder
          }
        });
      }
    }
  };

  const handleSubmitForm = (values: BasicInfoForm) => {
    const { contactType, tags, templateFields, allEmails, email, ...others } = values;
    
    // Convert empty strings to null for optional fields to avoid backend validation issues
    const processedOthers = Object.fromEntries(
      Object.entries(others).map(([key, value]) => [
        key,
        typeof value === 'string' && value.trim() === '' ? null : value
      ])
    );
    
    onSave({
      ...processedOthers,
      all_emails: allEmails,
      primary_email: email,
      contact_type: contactType,
      tags: tags,
      fields: Object.fromEntries(Object.entries(templateFields).map(([templateId, val]) => ([
        templateId, {
          text_value: val.textValue,
          choices: val.choices,
        }
      ])))
    });
  };

  // Create field configurations
  const fieldConfigs: Record<string, { component: ReactElement }> = {
    name: {
      component: (
        <Controller name="name" control={control} render={({ field: { ref, ...field }, fieldState }) => (
          <BasicInfoListItem 
            inputType="text" 
            title="Name" 
            inputValue={field.value}
            text={field.value}
            isDirty={fieldState.isDirty}
            onSave={field.onChange}
            sortId="name"
            draggable={isEditingOrder}
            isEditingOrder={isEditingOrder}
          />
        )} />
      )
    },
    email: {
      component: (
        <DependentController name="email" control={control} dependencies={['allEmails']}
          render={({ field: { ref, ...field }, fieldState, deps: [allEmailOptions] }) => (
            <BasicInfoListItem 
              inputType="select" 
              title="Primary Email" 
              inputValue={field.value}
              selectOptions={
                Array.isArray(allEmailOptions) 
                  ? allEmailOptions.map(e => ({ value: e, label: String(e) })) 
                  : []
              }
              text={field.value}
              isDirty={fieldState.isDirty}
              onSave={field.onChange}
              sortId="email"
              draggable={isEditingOrder}
              isEditingOrder={isEditingOrder}
            />
          )}
        />
      )
    },
    allEmails: {
      component: (
        <Controller name="allEmails" control={control} render={({ field: { ref, ...field }, fieldState }) => (
          <BasicInfoListItem 
            inputType="list" 
            title="All Emails" 
            inputValue={field.value}
            text={field.value.join(', ')}
            isDirty={fieldState.isDirty}
            onSave={field.onChange}
            sortId="allEmails"
            draggable={isEditingOrder}
            isEditingOrder={isEditingOrder}
          />
        )} />
      )
    },
    company: {
      component: (
        <Controller name="company" control={control} render={({ field: { ref, ...field }, fieldState }) => (
          <BasicInfoListItem 
            inputType="autocomplete"
            title="Company"
            selectOptions={orgOptions.map(o => ({ value: o.id, label: o.name }))}
            inputValue={field.value}
            text={orgOptions.find(o => o.id === field.value)?.name}
            onSave={field.onChange}
            onInputChange={onSetOrgString} 
            placeholder="Type to search all companies"
            noOptionsText="No companies match this search"
            isDirty={fieldState.isDirty}
            link={contactOrgPage}
            sortId="company"
            draggable={isEditingOrder}
            isEditingOrder={isEditingOrder}
          />
        )} />
      )
    },
    title: {
      component: (
        <Controller name="title" control={control} render={({ field: { ref, ...field }, fieldState }) => (
          <BasicInfoListItem 
            inputType="text" 
            title="Title"
            inputValue={field.value}
            text={field.value}
            onSave={field.onChange}
            isDirty={fieldState.isDirty}
            sortId="title"
            draggable={isEditingOrder}
            isEditingOrder={isEditingOrder}
          />
        )} />
      )
    },
    location: {
      component: (
        <Controller name="location" control={control} render={({ field: { ref, ...field }, fieldState }) => (
          <BasicInfoListItem 
            inputType="text" 
            title="Location"
            inputValue={field.value}
            text={field.value}
            onSave={field.onChange}
            isDirty={fieldState.isDirty}
            sortId="location"
            draggable={isEditingOrder}
            isEditingOrder={isEditingOrder}
          />
        )} />
      )
    },
    timezone: {
      component: (
        <Controller name="timezone" control={control} render={({ field: { ref, ...field }, fieldState }) => (
          <BasicInfoListItem 
            inputType="timezone" 
            title="Timezone"
            inputValue={field.value}
            text={formatTimezoneOptionsFromString(field.value).long}
            onSave={field.onChange}
            isDirty={fieldState.isDirty}
            sortId="timezone"
            draggable={isEditingOrder}
            isEditingOrder={isEditingOrder}
          />
        )} />
      )
    },
    linkedin_url: {
      component: (
        <Controller name="linkedin_url" control={control} render={({ field: { ref, ...field }, fieldState }) => (
          <BasicInfoListItem 
            inputType="text" 
            title="LinkedIn"
            inputValue={field.value}
            text={field.value}
            onSave={field.onChange}
            isDirty={fieldState.isDirty}
            link={field.value ? field.value.startsWith('http') ? field.value : `https://${field.value}` : ''}
            newTab
            sortId="linkedin_url"
            draggable={isEditingOrder}
            isEditingOrder={isEditingOrder}
          />
        )} />
      )
    },
    assistant_name: {
      component: (
        <Controller name="assistant_name" control={control} render={({ field: { ref, ...field }, fieldState }) => (
          <BasicInfoListItem 
            inputType="text" 
            title="Assistant Name"
            inputValue={field.value}
            text={field.value}
            onSave={field.onChange}
            isDirty={fieldState.isDirty}
            sortId="assistant_name"
            draggable={isEditingOrder}
            isEditingOrder={isEditingOrder}
          />
        )} />
      )
    },
    assistant_email: {
      component: (
        <Controller name="assistant_email" control={control} render={({ field: { ref, ...field }, fieldState }) => (
          <BasicInfoListItem 
            inputType="text" 
            title="Assistant Email"
            inputValue={field.value}
            text={field.value}
            onSave={field.onChange}
            isDirty={fieldState.isDirty}
            sortId="assistant_email"
            draggable={isEditingOrder}
            isEditingOrder={isEditingOrder}
          />
        )} />
      )
    },
    tags: {
      component: (
        <Controller name="tags" control={control} render={({ field: { ref, ...field }, fieldState }) => {
          const tagsValue = field.value || []; // Array of tag IDs (numbers)
          
          // Map tag IDs to Tag objects for display text
          const selectedTags = tagsValue
            .map(tagId => tagOptions.find(tag => tag.id === tagId))
            .filter((tag): tag is { id: number; name: string; color?: string } => !!tag);
          
          const CREATE_NEW_TAG_VALUE = 'CREATE_NEW_TAG';
          
          return (
            <BasicInfoListItem 
              inputType="multi-select"
              title="Contact Tags"
              inputValue={tagsValue}
              selectOptions={[
                ...tagOptions.map(tag => ({
                  value: tag.id,
                  label: tag.name,
                  color: tag.color
                })),
                {
                  value: CREATE_NEW_TAG_VALUE,
                  label: '+ Create New Tag',
                  color: undefined
                }
              ]}
              text={selectedTags.map(tag => tag.name).join(', ')}
              placeholder="Select tags..."
              onSave={(val) => {
                // Check if CREATE_NEW_TAG was selected
                const valArray = Array.isArray(val) ? val : (val ? [val] : []);
                const hasCreateNewTag = valArray.some(v => String(v) === CREATE_NEW_TAG_VALUE);
                if (hasCreateNewTag) {
                  onNavigateToSettings();
                  // Filter out CREATE_NEW_TAG_VALUE and keep existing tags
                  const tagsArray = tagsValue;
                  field.onChange(tagsArray);
                  return;
                }
                
                // Convert to array of tag IDs (numbers), filtering out CREATE_NEW_TAG_VALUE
                const tagsArray = valArray
                  .filter(v => String(v) !== CREATE_NEW_TAG_VALUE)
                  .map(v => typeof v === 'number' ? v : Number(v))
                  .filter(v => !isNaN(v));
                field.onChange(tagsArray);
              }}
              isDirty={fieldState.isDirty}
              sortId="tags"
              draggable={isEditingOrder}
              isEditingOrder={isEditingOrder}
            />
          );
        }} />
      )
    },
    // contactType: {
    //   component: (
    //     <Controller name="contactType" control={control} render={({ field: { ref, ...field }, fieldState }) => (
    //       <BasicInfoListItem 
    //         inputType="select"
    //         selectOptions={[
    //           { value: '', label: 'Unassigned', color: colors.black300 },
    //           ...typeOptions.map(o => ({ value: o.id, label: o.name, color: o.color }))
    //         ]}
    //         inputValue={field.value}
    //         title="Internal/External"
    //         text={typeOptions.find(o => o.id === field.value)?.name || 'Unassigned'}
    //         color={typeOptions.find(o => o.id === field.value)?.color || colors.black300}
    //         onSave={field.onChange}
    //         isDirty={fieldState.isDirty}
    //         sortId="contactType"
    //         draggable={isEditingOrder}
    //         isEditingOrder={isEditingOrder}
    //       />
    //     )} />
    //   )
    // }
  };

  // Add template fields to fieldConfigs
  fieldTemplates.forEach(template => {
    fieldConfigs[`templateFields.${template.id}`] = {
      component: (
        <Controller key={template.id} name={`templateFields.${template.id}`} control={control}
          render={({ field: { ref, ...field }, fieldState }) => (
            <BasicInfoListItem
              inputType={['single_select', 'multi_select'].includes(template.value_type) ? "select"
                : template.value_type === 'date_type' ? 'text'
                  : 'text'
              }
              selectOptions={template.contact_field_choices.map((o, idx) => ({ value: idx, label: o.label }))}
              inputValue={template.value_type === 'single_select' ? (field.value?.choices?.[0] ?? '')
                : (field.value?.choices || field.value?.textValue || null)}
              title={template.label}
              text={field.value?.choices
                ? field.value.choices.map(c => template.contact_field_choices[c].label).join(', ')
                : field.value?.textValue
                || ''}
              onSave={(val) => {
                const v: { textValue?: string; choices?: (number | string)[] } = {};
                if (['single_select', 'multi_select'].includes(template.value_type)) {
                  v.choices = Array.isArray(val) ? val : typeof val === 'number' ? [val] : [];
                } else {
                  v.textValue = val?.toString();
                }
                field.onChange(v);
              }}
              isDirty={fieldState.isDirty}
              sortId={`templateFields.${template.id}`}
              draggable={isEditingOrder}
              isEditingOrder={isEditingOrder}
            />
          )}
        />
      )
    };
  });

  if (isEditingOrder) {
    return (
      <DndContext
        sensors={[sensors]}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis]}
      >
        <SortableContext
          items={fieldOrder}
          strategy={verticalListSortingStrategy}
        >
          <WidgetList>
            {fieldOrder.map((fieldKey, index) => {
              const fieldConfig = fieldConfigs[fieldKey];
              return fieldConfig ? (
                <div key={`${fieldKey}-${index}`}>
                  {fieldConfig.component}
                </div>
              ) : null;
            })}

            {formState.isDirty && (
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  paddingTop: 3,
                  paddingBottom: 2,
                  gap: 2,
                  marginTop: 2,
                  backgroundColor: colors.white900,
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 10,
                  marginLeft: -1,
                  marginRight: -1,

                  // paddingLeft: 1,
                  // paddingRight: 1,
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: -24,
                    right: -24,
                    height: '1px',
                    backgroundColor: colors.black200,
                  },

                  boxShadow: 'inset 0 6px 6px -6px rgba(0, 0, 0, 0.1)'
                }}>
                <CabButton
                  // size="small"
                  onClick={() => reset()}
                  buttonType="tertiary"
                  sx={{ flex: 1}}
                >
                  Discard Changes
                </CabButton>
                <CabButton
                  // size="small"
                  onClick={handleSubmit(handleSubmitForm)}
                  sx={{ flex: 1}}
                >
                  Save Fields
                </CabButton>
              </Box>
            )}
          </WidgetList>
        </SortableContext>
        <DragOverlay>
          <div style={{ opacity: 0.8, transform: 'rotate(5deg)' }}>
            {/* The dragged item will be rendered here */}
          </div>
        </DragOverlay>
      </DndContext>
    );
  }

  return (
    <WidgetList>
      {fieldOrder.map((fieldKey, index) => {
        const fieldConfig = fieldConfigs[fieldKey];
        return fieldConfig ? (
          <div key={`${fieldKey}-${index}`}>
            {fieldConfig.component}
          </div>
        ) : null;
      })}
      {formState.isDirty && (
        <Box
          sx={{
            display: "flex",
            width: "100%",
            paddingTop: 3,
            paddingBottom: 2,
            gap: 2,
            marginTop: 2,
            backgroundColor: colors.white900,
            position: 'sticky',
            bottom: 0,
            zIndex: 10,
            marginLeft: -1,
            marginRight: -1,

            // paddingLeft: 1,
            // paddingRight: 1,
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: -24,
              right: -24,
              height: '1px',
              backgroundColor: colors.black200,
            },

            boxShadow: 'inset 0 6px 6px -6px rgba(0, 0, 0, 0.1)'
          }}>
          <CabButton
            // size="small"
            onClick={() => reset()}
            buttonType="tertiary"
            sx={{ flex: 1, backgroundColor: colors.white900 }}
          >
            Discard Changes
          </CabButton>
          <CabButton
            // size="small"
            onClick={handleSubmit(handleSubmitForm)}
            sx={{ flex: 1}}
          >
            Save Fields
          </CabButton>
        </Box>
      )}
    </WidgetList>
  );
};

const ActivitiesView = ({ activities, onDeleteActivity, onUpdateActivity, onClickActivity, onClickEvent }: {
  activities: (ContactTask | ContactNote | AnalyticsEvent)[],
  onDeleteActivity: (id: ContactTask['id'] | ContactNote['id'], activityType: 'task'|'note') => void;
  onUpdateActivity: Trigger<AppCrmContactTasksPartialUpdateApiArg, ContactTask>;
  onClickActivity: (id: ContactTask['id'], activityType: 'task'|'note') => void;
  onClickEvent: (event: AnalyticsEvent) => void;
}) => (
  activities.length ? (
    <WidgetList>
      {activities.map(r => (
        'assignee' in r ? (
          <ReminderListItem
            key={`task-${r.id}`}
            title={r.title}
            dateAdded={r.created_at}
            dueDate={r.due_date}
            checked={r.status === 'Completed'}
            onClick={() => onClickActivity(r.id, 'task')}
            onCheck={(check) => onUpdateActivity({
              id: r.id,
              patchedContactTask: { status: check ? 'Completed' : 'To-Do' },
            })}
            onDelete={() => onDeleteActivity(r.id, 'task')}
          />
        ) : 'body' in r ? (
          <NoteListItem
            key={`note-${r.id}`}
            title={r.body_plain_text || 'UNKNOWN'}
            text={r.created_at && DateTime.fromISO(r.created_at).toLocaleString(DateTime.DATETIME_FULL)}
            onClick={() => onClickActivity(r.id, 'note')}
            onDelete={() => onDeleteActivity(r.id, 'note')}
          />
        ) : 'attendees' in r ? (
          <MeetingListItem 
            key={`event-${r.id}`} 
            title={r.title} 
            startDate={r.start_date}
            attendeeCount={r.attendees.length}
            onClick={() => onClickEvent(r)}
          />
        ) : null
      ))}
    </WidgetList>
  ) : (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: "100%",
        justifyContent: "center",
        alignItems: "center",
        gap: 2
      }}>
      <Typography color={colors.black800}>There are no activities for this contact</Typography>
    </Box>
  )
);


const TasksView = ({ tasks, onDeleteTask, onUpdateTask, onClickTask, onAddTask }: {
  tasks: ContactTask[], onDeleteTask: (id: ContactTask['id']) => void;
  onUpdateTask: Trigger<AppCrmContactTasksPartialUpdateApiArg, ContactTask>;
  onClickTask: (id: ContactTask['id']) => void;
  onAddTask: () => void;
}) => (
  tasks.length ? (
    <WidgetList>
      {tasks.map(r => (
        <ReminderListItem
          key={r.id}
          title={r.title}
          dateAdded={r.created_at}
          dueDate={r.due_date}
          checked={r.status === 'Completed'}
          onClick={() => onClickTask(r.id)}
          onCheck={(check) => onUpdateTask({ id: r.id, patchedContactTask: { status: check ? 'Completed' : 'To-Do' } })}
          onDelete={() => onDeleteTask(r.id)}
        />
      ))}
    </WidgetList>
  ) : (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: "100%",
        justifyContent: "center",
        alignItems: "center",
        gap: 2
      }}>
      <Typography color={colors.black800}>There are no tasks for this contact</Typography>
      <CabButton buttonType="tertiary" onClick={onAddTask}>Add Task</CabButton>
    </Box>
  )
);

const MeetingsView = ({ 
  events, 
  onClickEvent 
}: { 
  events: AnalyticsEvent[]; 
  onClickEvent: (event: AnalyticsEvent) => void 
}) => (
  events && events.length ? (
    <WidgetList>
      {events.map(e => (
        <MeetingListItem 
          key={e.id} 
          title={e.title} 
          startDate={e.start_date}
          attendeeCount={e.attendees.length}
          onClick={() => onClickEvent(e)}
        />
      ))}
    </WidgetList>
  ) : (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: "100%",
        justifyContent: "center",
        alignItems: "center",
        gap: 2
      }}>
      <Typography color={colors.black800}>There are no events for this contact</Typography>
    </Box>
  )
);

const NotesView = ({ notes, onClickNote, onDeleteNote, onAddNote }: {
  notes: ContactNote[], onDeleteNote: (id: ContactNote['id']) => void;
  onClickNote: (id: ContactNote['id']) => void;
  onAddNote: () => void;
}) => (
  notes.length ? (
    <WidgetList>
      {notes.map(n => (
        <NoteListItem
          key={n.id}
          title={n.body_plain_text || 'UNKNOWN'}
          text={n.created_at && DateTime.fromISO(n.created_at).toLocaleString(DateTime.DATETIME_FULL)}
          onClick={() => onClickNote(n.id)}
          onDelete={() => onDeleteNote(n.id)}
        />
      ))}
    </WidgetList>
  ) : (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: "100%",
        justifyContent: "center",
        alignItems: "center",
        gap: 2
      }}>
      <Typography color={colors.black800}>There are no notes for this contact</Typography>
      <CabButton buttonType="tertiary" onClick={onAddNote}>Add Note</CabButton>
    </Box>
  )
);

const ReminderListItem = ({ title, text, checked, onEdit, onCheck, onDelete, onClick, dateAdded, dueDate }: {
  title: string; text?: string | null; checked?: boolean; onEdit?: () => void;
  onCheck: (check: boolean) => void; onDelete: () => void; onClick: () => void;
  dateAdded?: string; dueDate?: string | null;
}) => (
  <WidgetListItem density="comfortable" secondaryAction={
    <CabIcon Icon={IoClose} onClick={onDelete} sx={{ marginTop: -2.5 }} />
  }>
    <ListItemIcon sx={{ minWidth: 35, alignSelf: 'flex-start', marginTop: 0.65 }}>
      <CabCheckbox checked={checked} onChange={(e, c) => onCheck(c)} />
    </ListItemIcon>
    <ListItemText
      primary={<Link href="#" color={colors.black900} underline="hover" onClick={onClick} sx={{ 
        display: 'block',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
      }}>{title}</Link>}
      secondary={
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          {dateAdded && (
            <Typography variant="body2" sx={{
              color: "text.secondary"
            }}>
              Added {DateTime.fromISO(dateAdded).toLocaleString(DateTime.DATETIME_FULL)}
            </Typography>
          )}
          {dueDate && (
            <CabToggleChip
              label={`Due ${DateTime.fromISO(dueDate).toLocaleString(DateTime.DATE_FULL)}`}
              onClick={onClick}
              selected
              chipColor={DateTime.fromISO(dueDate) < DateTime.now() ? colors.sunset800 : colors.lavendar400}
              translateColor
              sx={{ height: 20, fontSize: '12px' }}
            />
          )}
        </Box>
      }
      slotProps={{
        primary: { sx: { textDecoration: checked ? 'line-through' : undefined } }
      }}
    />
  </WidgetListItem>
);

const NoteListItem = ({ title, text, onDelete, onClick }: {
  title: string; text?: string; onDelete: () => void; onClick: () => void;
}) => (
  <WidgetListItem density="comfortable" secondaryAction={
    <CabIcon Icon={IoClose} onClick={onDelete} sx={{ marginTop: -2.5 }} />
  }>
    <ListItemIcon sx={{ minWidth: 35, alignSelf: 'flex-start', marginTop: 0.75 }}>
      <CabIcon Icon={IoDocumentTextOutline} sx={{ fontSize: 22 }} />
    </ListItemIcon>
    <ListItemText
      primary={<Link href="#" color={colors.black900} underline="hover" onClick={onClick}>{title}</Link>}
      secondary={text}
      slotProps={{
        primary: { noWrap: true }
      }}
    />
  </WidgetListItem>
);

const MeetingListItem = ({ title, startDate, attendeeCount, onEdit, onClick }: {
  title: string, startDate: string, attendeeCount: number, onEdit?: () => void, onClick?: () => void
}) => (
  <WidgetListItem density="comfortable" onClick={onClick}>
    <ListItemIcon sx={{ minWidth: 35, alignSelf: 'flex-start', marginTop: 0.9 }}>
      <CabIcon Icon={IoCalendarOutline} sx={{ fontSize: 20, marginLeft: 0.25 }} />
    </ListItemIcon>
    <ListItemText
      primary={title}
      secondary={
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            marginTop: .5
          }}>
          <Box
            sx={{
              display: 'flex',
              gap: .25
            }}>
            <CabIcon 
              Icon={IoTimeOutline} 
              sx={{fontSize: 15, color: colors.black800, marginRight: "2px", marginTop: "1px" }} 
            />
            <Typography
              variant='body2'
              sx={{
                lineHeight: '16px',
                color: colors.black800
              }}>
              {`${DateTime.fromISO(startDate).toLocaleString(DateTime.DATETIME_FULL)}`}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: .25
            }}>
            <CabIcon 
              Icon={IoPeopleOutline} 
              sx={{fontSize: 15, color: colors.black800, marginRight: "2px", marginTop: "1px" }}
            />
            <Typography
              variant='body2'
              sx={{
                lineHeight: '16px',
                color: colors.black800
              }}>
              {`${attendeeCount} attendees`}
            </Typography>
          </Box>
        </Box>
      }
    />
  </WidgetListItem>
);

const AddNoteModal = ({ open, onClose, onSubmit, note }: {
  open: boolean; onClose: () => void; onSubmit: ({ body} : { body: string }) => void;
  note?: ContactNote,
}) => {
  const { control, handleSubmit, formState } = useForm<{ body: string }>({ defaultValues: {
    body: note?.body && isRT(note.body) ? note.body : EMPTY_STATE,
  } });

  return (
    <CabModal open={open} title={`${note ? 'Edit' : 'New'} Note`}
      sx={{ '& .MuiDialog-paper': { width: '90vw', maxWidth: '850px' } }}
      actionButtons={<>
        <CabButton
          buttonType="secondary"
          onClick={onClose}
        >
          Cancel
        </CabButton>
        <CabButton onClick={handleSubmit(onSubmit)} disabled={!formState.isValid}>
          {note ? 'Save' : 'Add'}
        </CabButton>
      </>}
    >
      <Box>
        <Controller name="body" control={control} rules={{ required: true }}
          render={({ field: { ref, ...field }}) => (
            <CabRTEditor
              placeholder="Type to add notes..."
              {...field}
              editorState={field.value}
              sx={{
                flex: 1, 
                width: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '& > div:last-child': {
                  flex: 1,
                  overflow: 'auto',
                  minHeight: 0
                },
                '& .editor-input': {
                  height: '100% !important',
                  overflow: 'auto !important',
                  minHeight: '350px !important',
                }
              }}
            />
          )}
        />

      </Box>
    </CabModal>
  );
};

const AddTaskModal = ({ open, onClose, onSubmit, task }: {
  open: boolean; 
  onClose: () => void; 
  onSubmit: ({ title, due_date } : { title: string; due_date?: string | null }) => void;
  task?: ContactTask,
}) => {
  const [showDueDateInputs, setShowDueDateInputs] = useState(!!task?.due_date);
  
  const {
    control, handleSubmit, formState, setValue
  } = useForm<{ title: string; dueDate: string | null; dueTime: string }>({ 
    defaultValues: {
      title: task?.title || '',
      dueDate: task?.due_date ? DateTime.fromISO(task.due_date).toISODate() : null,
      dueTime: task?.due_date ? DateTime.fromISO(task.due_date).toFormat('HH:mm') : '',
    } 
  });

  const localTimeZone = getLocalTimeZone();
  const timezoneDisplay = localTimeZone 
    ? `${localTimeZone.abbreviation} (GMT${localTimeZone.currentTimeOffsetInMinutes >= 0 ? '+' : ''}${
      localTimeZone.currentTimeOffsetInMinutes / 60
    })` 
    : '';

  const handleAddDueDate = () => {
    const tomorrow = DateTime.now().plus({ days: 1 });
    setValue('dueDate', tomorrow.toISODate());
    setValue('dueTime', '17:00'); // 5 PM
    setShowDueDateInputs(true);
  };

  const handleRemoveDueDate = () => {
    setValue('dueDate', null);
    setValue('dueTime', '');
    setShowDueDateInputs(false);
  };

  const handleFormSubmit = (data: { title: string; dueDate: string | null; dueTime: string }) => {
    let due_date: string | null = null;
    
    if (data.dueDate) {
      // Parse the date (which might be a full ISO string or just a date)
      const baseDate = DateTime.fromISO(data.dueDate);
      
      if (data.dueTime) {
        // Combine date and time
        const [hours, minutes] = data.dueTime.split(':');
        const dateTime = baseDate.set({ hour: parseInt(hours), minute: parseInt(minutes) });
        due_date = dateTime.toISO();
      } else {
        // Date only - set to end of day
        due_date = baseDate.endOf('day').toISO();
      }
    }
    
    onSubmit({ title: data.title, due_date });
  };

  return (
    <CabModal open={open} title={`${task ? 'Edit' : 'New'} Task`}
      sx={{ '& .MuiDialog-paper': { width: '90vw', maxWidth: '850px' } }}
      actionButtons={<>
        <CabButton
          buttonType="secondary"
          onClick={onClose}
        >
          Cancel
        </CabButton>
        <CabButton onClick={handleSubmit(handleFormSubmit)} disabled={!formState.isValid}>
          {task ? 'Save' : 'Add'}
        </CabButton>
      </>}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3
        }}>
        <FormController name="title" control={control} rules={{ required: true }}
          render={({ field: { ref, ...field }}) => (
            <CabTextInput inputRef={ref} {...field}
              multiline
              minRows={15}
              sx={{ height: '340px', minHeight: '340px' }}
            />
          )}
        />
        
        {showDueDateInputs ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}>
            <Typography variant="h3" color={colors.black900} sx={{
              fontSize: 16
            }}>
              Due By
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                width: "fit-content"
              }}>
              <CabButton
                buttonType="secondary"
                onClick={handleRemoveDueDate}
                sx={{ 
                  minWidth: '32px', 
                  width: '32px', 
                  height: '32px', 
                  borderRadius: '50%',
                  padding: '2px 0 0 0'
                }}
              >
                <IoClose />
              </CabButton>
              <FormController name="dueDate" control={control}
                render={({ field }) => (
                  <CabDatePicker
                    value={field.value}
                    onChange={field.onChange}
                    clearable
                  />
                )}
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1
                }}>
                <FormController name="dueTime" control={control}
                  render={({ field }) => (
                    <CabTimePicker
                      pickerTime={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <Typography
                  variant="body1"
                  sx={{
                    color: "text.disabled",
                    whiteSpace: 'nowrap'
                  }}>
                  {timezoneDisplay}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{
            width: "fit-content"
          }}>
            <CabButton
              buttonType="secondary"
              onClick={handleAddDueDate}
              sx={{ 
                borderRadius: '20px',
                textTransform: 'none',
                padding: '8px 16px'
              }}
              startIcon={<IoAdd />}
            >
              Add Due Date
            </CabButton>
          </Box>
        )}
      </Box>
    </CabModal>
  );
};

const EditRelationshipsModal = ({ open, onClose, onSubmit, leaderOptions, leaderRelationships }: {
  open: boolean; onClose:() => void;
  onSubmit: ({ leaderRelationships }: { leaderRelationships: Pick<Leader, 'id'>[] }) => void;
  leaderOptions: Leader[], leaderRelationships: Leader[],
}) => {
  const { control, handleSubmit, formState, watch } = useForm<{ leaderRelationships: Pick<Leader, 'id'>[] }>({
    defaultValues: { leaderRelationships }
  });

  const { fields: relationshipFields, append, remove } = useFieldArray({
    control, name: 'leaderRelationships',
    // need to specify this or it will override the leader `id`
    keyName: 'fieldId'
  });

  const leaderRelValues = watch('leaderRelationships');
  const lastFieldId = leaderRelValues.at(-1)?.id;

  const cabExecOptions = useMemo(() => leaderOptions.map(leader => {
    return {
      value: leader.id,
      label: `${leader.first_name} ${leader.last_name}`,
      icon: <CabAvatar
        src={getLeaderIconSrc(leader)}
        color={leader.color}
        name={`${leader.first_name}
        ${leader.last_name}`}
        size="small"
      />
    };
  }), [leaderOptions]);

  const availableOptions = cabExecOptions.filter(o => !leaderRelValues.find(f => f.id === o.value));

  return (
    <CabModal open={open} title="Teammate Relationships"
      actionButtons={<>
        <CabButton
          buttonType="secondary"
          onClick={onClose}
        >
          Cancel
        </CabButton>
        <CabButton onClick={handleSubmit(onSubmit)} disabled={!formState.isValid || formState.isSubmitting}>
          {formState.isSubmitting ? <CabSpinner scale={1} /> : 'Save'}
        </CabButton>
      </>}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column"
        }}>
        {relationshipFields.map((relField, index) => (
          <FormController key={relField.fieldId} name={`leaderRelationships.${index}.id`} label=""
            control={control} rules={{ required: true, validate: (v) => v !== -1 }}
            render={({ field: { ref, ...field }}) => {
              const selectedOption = cabExecOptions.find(o => o.value === field.value);
              const options = selectedOption
                ? [selectedOption, ...availableOptions.filter(ao => ao.value !== selectedOption.value)]
                : availableOptions;

              return (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1
                  }}>
                  <CabExecPicker
                    {...field}
                    value={field.value === -1 ? '' : field.value}
                    options={options}
                    placeholder="Select a Teammate"
                    fullWidth
                  />
                  <CabIcon Icon={IoCloseCircleOutline} onClick={() => remove(index)} sx={{ marginLeft: 2 }} />
                </Box>
              );
            }}
          />
        ))}
        {availableOptions.length > 0 && (
          <CabButton
            onClick={() => append({ id: -1 })}
            icon={<CabIcon Icon={IoAdd} />}
            buttonType="tertiary"
            sx={{ width: '100%', marginTop: 2 }}
            disabled={lastFieldId === -1}
          >
            Add New Relationship
          </CabButton>
        )}
      </Box>
    </CabModal>
  );
};


export default CRMContactView;
